import {Alert, Modal} from '@forecast-it/design-system';
import React, {useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import type {CreateCustomFieldDefinitionMutation$data as CreateCustomFieldDefinitionMutationResponse} from '../../../../mutations/custom-fields/__generated__/CreateCustomFieldDefinitionMutation.graphql';
import CreateCustomFieldDefinitionMutation from '../../../../mutations/custom-fields/CreateCustomFieldDefinitionMutation';
import {ModalProps} from '../../../shared/components/modals/types';
import {PartialBy} from '../../../shared/util/type-util/PartialBy';
import Util from '../../../shared/util/util';
import {useAvaRefOptions} from '../components/ava-custom-field-dropdown/useAvaRefOptions';
import {CustomFieldDefinitionForm} from '../components/CustomFieldDefinitionForm';
import {CustomFieldType, EntityType} from '../Constants';
import {DEFAULT_LIST_VISIBILITY, ListVisibility, toListVisibilityInput} from '../components/listVisibility';
import {AlertMessage, CustomFieldDefinition} from '../types';
import type {CustomFieldDefinitionCreateModal_company$data as CustomFieldDefinitionCreateModal_company} from './__generated__/CustomFieldDefinitionCreateModal_company.graphql';
import {showCustomFieldDropdownOptionsModal} from './dropdown-options-modal/CustomFieldDropdownOptionsModal';

export interface CustomFieldDefinitionCreateModalInput {
	entityType: EntityType;
	setAlertMessage: (message: AlertMessage) => void;
}

type CustomFieldDefinitionCreateModalProps = ModalProps<
	{company: CustomFieldDefinitionCreateModal_company} & CustomFieldDefinitionCreateModalInput
>;

const CustomFieldDefinitionCreateModalInternal: React.FC<CustomFieldDefinitionCreateModalProps> = ({
	entityType: defaultEntityType,
	company,
	setAlertMessage,
	closeModal,
}) => {
	const {formatMessage} = useIntl();

	const [entityType, setEntityType] = useState<EntityType>(defaultEntityType);
	const [fieldKey, setFieldKey] = useState('');
	const [displayName, setDisplayName] = useState('');
	const [readOnly, setReadOnly] = useState(false);
	const [creating, setCreating] = useState(false);
	const [customFieldType, setCustomFieldType] = useState<CustomFieldType>(CustomFieldType.STRING);
	const [listVisibility, setListVisibility] = useState<ListVisibility>(DEFAULT_LIST_VISIBILITY);

	// TODO: fix this type coersion;
	const customFieldDefinitions = (company?.customFieldDefinitionsV2?.edges?.map(edge => edge?.node) || []) as PartialBy<
		CustomFieldDefinition,
		'id'
	>[];

	const savedCustomFields = customFieldDefinitions.filter(cd => !!cd?.id);

	const {
		selectedOption: selectedAvaRef,
		setSelectedOption: selectAvaRefOption,
		options: avaRefOptions,
	} = useAvaRefOptions(entityType, customFieldDefinitions);

	const onKeyChange = (newKey: string) => {
		setFieldKey(newKey.replace(/[^A-Za-z0-9-_]/g, ''));
	};

	const create = () => {
		setCreating(true);
		Util.CommitMutation(
			CreateCustomFieldDefinitionMutation,
			{
				key: fieldKey,
				displayName,
				readOnly:
					readOnly &&
					(customFieldType === CustomFieldType.STRING ||
						customFieldType === CustomFieldType.INTEGER ||
						customFieldType === CustomFieldType.DECIMAL ||
						customFieldType === CustomFieldType.DATE),
				entityType: entityType,
				companyId: company.id,
				type: customFieldType,
				avaRef: selectedAvaRef.value,
				...toListVisibilityInput(entityType, listVisibility),
			},
			({createCustomFieldDefinition}: CreateCustomFieldDefinitionMutationResponse) => {
				closeModal();
				const newDefinition = createCustomFieldDefinition?.customFieldDefinition?.node as CustomFieldDefinition | null;
				if (newDefinition && newDefinition.type === CustomFieldType.DROPDOWN) {
					showCustomFieldDropdownOptionsModal({
						customFieldDefinition: newDefinition,
						setAlertMessage,
					});
				} else {
					setAlertMessage({
						text: formatMessage({id: 'settings.custom-fields.create_alert'}, {name: displayName}),
						type: Alert.TYPE.SUCCESS,
					});
				}
				setCreating(false);
			},
			true,
			null,
			// The default error handling still runs. Create is re-enabled afterwards so the admin can
			// correct the form and retry, unless the fallback has scheduled its own retry, in which
			// case this attempt is not over yet.
			(transaction: unknown, onFailure?: (t: unknown) => boolean | void) => {
				const retryInFlight = onFailure ? onFailure(transaction) === true : false;
				if (!retryInFlight) {
					setCreating(false);
				}
			}
		);
	};

	const errorMessage = useMemo(() => {
		const isDuplicateKey = !!savedCustomFields?.find(
			cf => cf?.entityType === entityType && cf?.key.toLowerCase() === fieldKey.toLowerCase()
		);

		if (isDuplicateKey) {
			return formatMessage({id: 'settings.custom-fields.already_exists'});
		}
	}, [savedCustomFields, fieldKey]);

	const isValid = entityType && fieldKey && displayName && !errorMessage;

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				<FormattedMessage id={'settings.custom-fields.new_custom_field_title'} />
			</Modal.Title>
			<Modal.Content>
				<CustomFieldDefinitionForm
					entityType={entityType}
					setEntityType={setEntityType}
					displayName={displayName}
					setDisplayName={setDisplayName}
					fieldKey={fieldKey}
					setFieldKey={onKeyChange}
					customFieldType={customFieldType}
					setCustomFieldType={setCustomFieldType}
					avaRefOptions={avaRefOptions}
					selectedAvaRef={selectedAvaRef}
					selectAvaRefOption={selectAvaRefOption}
					readOnly={readOnly}
					setReadOnly={setReadOnly}
					errorMessage={errorMessage}
					listVisibility={listVisibility}
					setListVisibility={setListVisibility}
				/>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton disabled={!isValid || creating} onPress={create} dataCy="button-save">
					{'Create Custom Field'}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const CustomFieldDefinitionCreateModal = createFragmentContainer(CustomFieldDefinitionCreateModalInternal, {
	company: graphql`
		fragment CustomFieldDefinitionCreateModal_company on Company {
			id
			customFieldDefinitionsV2(first: 1000) @connection(key: "Company_customFieldDefinitionsV2") {
				edges {
					node {
						id
						key
						type
						displayName
						entityType
						avaRef
						... on TextCustomField {
							readOnly
						}
						... on IntegerCustomField {
							readOnly
						}
						... on DecimalCustomField {
							readOnly
						}
						... on DateCustomField {
							readOnly
						}
					}
				}
			}
		}
	`,
});
